import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { 
  Building2, RefreshCw, Check, X, ArrowLeftRight, Calendar, AlertTriangle, Shield,
  LayoutDashboard, Upload, List, ClipboardCheck, FileBarChart, CreditCard, LogOut, Menu
} from "lucide-react";

export default function TransfersPage() {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [pendingMatches, setPendingMatches] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, statsRes] = await Promise.all([
        axios.get(`${API}/transfers/pending`, { headers, withCredentials: true }),
        axios.get(`${API}/transfers/stats`, { headers, withCredentials: true })
      ]);
      setPendingMatches(pendingRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching transfers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      const response = await axios.get(`${API}/transfers/scan?limit=100`, { headers, withCredentials: true });
      toast.success(`Scan complete: ${response.data.new_matches_created} new potential transfers found`);
      fetchData();
    } catch (error) {
      toast.error("Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const handleConfirm = async (matchId) => {
    setProcessingId(matchId);
    try {
      await axios.post(`${API}/transfers/${matchId}/confirm`, {}, { headers, withCredentials: true });
      toast.success("Transfer confirmed - transactions excluded from income/expenses");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm");
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (matchId) => {
    setProcessingId(matchId);
    try {
      const formData = new FormData();
      formData.append("reason", "Not an internal transfer");
      await axios.post(`${API}/transfers/${matchId}/reject`, formData, { headers, withCredentials: true });
      toast.success("Transfer rejected");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject");
    } finally {
      setProcessingId(null);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.85) return "text-emerald-500";
    if (confidence >= 0.7) return "text-amber-500";
    return "text-red-500";
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const NavLink = ({ to, icon: Icon, children, active, badge }) => (
    <Link to={to} className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${active ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'}`}>
      <Icon className="w-5 h-5" />
      <span className="text-sm font-medium">{children}</span>
      {badge > 0 && <span className="ml-auto bg-[#FCD34D] text-[#1C1917] text-xs font-bold px-2 py-0.5 rounded-full">{badge}</span>}
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#121214] border-r border-zinc-800 hidden lg:flex flex-col">
        <div className="p-6 border-b border-zinc-800">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#1C1917]" />
            </div>
            <div>
              <h1 className="font-semibold text-white">BankFlow Control</h1>
              <p className="text-xs text-zinc-500">Financial Analysis</p>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <NavLink to="/dashboard" icon={LayoutDashboard}>Dashboard</NavLink>
          <NavLink to="/upload" icon={Upload}>Upload Statements</NavLink>
          <NavLink to="/transactions" icon={List}>Transactions</NavLink>
          <NavLink to="/transfers" icon={ArrowLeftRight} active badge={pendingMatches.length}>Transfer Detection</NavLink>
          <NavLink to="/review" icon={ClipboardCheck}>Review Queue</NavLink>
          <NavLink to="/reports" icon={FileBarChart}>Reports</NavLink>
          <NavLink to="/accounts" icon={CreditCard}>Bank Accounts</NavLink>
          {user?.role === 'admin' && <NavLink to="/admin" icon={Shield}>Admin Settings</NavLink>}
        </nav>
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-sm font-medium text-white">{user?.name?.charAt(0) || 'U'}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#121214] border-b border-zinc-800">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#1C1917]" />
            </div>
            <span className="font-semibold text-white">BankFlow</span>
          </Link>
          <Button variant="ghost" size="icon" className="text-zinc-400" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Internal Transfer Detection</h1>
              <p className="text-zinc-400 text-sm mt-1">
                Automatically detect and confirm transfers between your accounts
              </p>
            </div>
            <Button 
              onClick={handleScan} 
              disabled={scanning}
              className="bg-[#FCD34D] text-[#1C1917] hover:bg-[#FBBF24]"
              data-testid="scan-transfers-btn"
            >
              {scanning ? (
                <div className="w-4 h-4 border-2 border-[#1C1917] border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Scan for Transfers
            </Button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="bg-[#121214] border-zinc-800">
                <CardContent className="p-4">
                  <p className="text-sm text-zinc-400 mb-1">Pending Review</p>
                  <p className="text-2xl font-bold text-amber-500 font-mono">{stats.pending_matches}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#121214] border-zinc-800">
                <CardContent className="p-4">
                  <p className="text-sm text-zinc-400 mb-1">Confirmed</p>
                  <p className="text-2xl font-bold text-emerald-500 font-mono">{stats.confirmed_matches}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#121214] border-zinc-800">
                <CardContent className="p-4">
                  <p className="text-sm text-zinc-400 mb-1">Rejected</p>
                  <p className="text-2xl font-bold text-red-500 font-mono">{stats.rejected_matches}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#121214] border-zinc-800">
                <CardContent className="p-4">
                  <p className="text-sm text-zinc-400 mb-1">Transfer Value</p>
                  <p className="text-2xl font-bold text-indigo-500 font-mono">{formatCurrency(stats.total_transfer_value)}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* How It Works */}
          <Card className="bg-[#121214] border-zinc-800 mb-6">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                How Transfer Detection Works
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div className="p-3 bg-zinc-900/50 rounded-lg">
                  <p className="text-zinc-300 font-medium mb-1">Amount Matching</p>
                  <p className="text-zinc-500">Finds transactions with matching amounts (±1% for fees)</p>
                </div>
                <div className="p-3 bg-zinc-900/50 rounded-lg">
                  <p className="text-zinc-300 font-medium mb-1">Date Proximity</p>
                  <p className="text-zinc-500">Transactions within 3 days of each other</p>
                </div>
                <div className="p-3 bg-zinc-900/50 rounded-lg">
                  <p className="text-zinc-300 font-medium mb-1">Debit/Credit Mirror</p>
                  <p className="text-zinc-500">One account debited, another credited</p>
                </div>
                <div className="p-3 bg-zinc-900/50 rounded-lg">
                  <p className="text-zinc-300 font-medium mb-1">Different Accounts</p>
                  <p className="text-zinc-500">Must be between two of your own accounts</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pending Matches */}
          <Card className="bg-[#121214] border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg text-white">Pending Transfer Matches</CardTitle>
              <CardDescription className="text-zinc-500">
                Review and confirm or reject detected transfer pairs
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="w-8 h-8 border-2 border-[#FCD34D] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : pendingMatches.length > 0 ? (
                <div className="space-y-4">
                  {pendingMatches.map((match) => (
                    <div key={match.match_id} className="border border-zinc-800 rounded-lg p-4" data-testid={`match-${match.match_id}`}>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                            <ArrowLeftRight className="w-5 h-5 text-indigo-500" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{formatCurrency(match.amount)}</p>
                            <p className="text-xs text-zinc-500">Transfer between accounts</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-mono ${getConfidenceColor(match.match_confidence)}`}>
                            {Math.round(match.match_confidence * 100)}% confidence
                          </p>
                          <Progress value={match.match_confidence * 100} className="w-24 h-1.5 mt-1" />
                        </div>
                      </div>

                      {/* Transaction Pair */}
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        {/* Transaction 1 - Debit */}
                        <div className="p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <Badge className="badge-error text-xs">DEBIT</Badge>
                            <span className="text-xs text-zinc-500">{match.account_1_name}</span>
                          </div>
                          <p className="text-sm text-white truncate">{match.description_1}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-zinc-500">{formatDate(match.date_1)}</span>
                            <span className="text-sm font-mono text-red-500">-{formatCurrency(match.amount)}</span>
                          </div>
                        </div>

                        {/* Transaction 2 - Credit */}
                        <div className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <Badge className="badge-success text-xs">CREDIT</Badge>
                            <span className="text-xs text-zinc-500">{match.account_2_name}</span>
                          </div>
                          <p className="text-sm text-white truncate">{match.description_2}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-zinc-500">{formatDate(match.date_2)}</span>
                            <span className="text-sm font-mono text-emerald-500">+{formatCurrency(match.amount)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Match Reasons */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {match.match_reasons?.map((reason, idx) => (
                          <span key={idx} className="text-xs px-2 py-1 bg-zinc-800 text-zinc-400 rounded">
                            {reason}
                          </span>
                        ))}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handleConfirm(match.match_id)}
                          disabled={processingId === match.match_id}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`confirm-${match.match_id}`}
                        >
                          {processingId === match.match_id ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              <Check className="w-4 h-4 mr-2" />
                              Confirm Transfer
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => handleReject(match.match_id)}
                          disabled={processingId === match.match_id}
                          variant="outline"
                          className="flex-1 border-red-500/30 text-red-500 hover:bg-red-500/10"
                          data-testid={`reject-${match.match_id}`}
                        >
                          <X className="w-4 h-4 mr-2" />
                          Not a Transfer
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-emerald-500" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">No Pending Transfers</h3>
                  <p className="text-zinc-400 text-sm mb-4">
                    All detected transfers have been reviewed. Click "Scan for Transfers" to check for new matches.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
